import * as React from 'react';
import {
  Create,
  useListController,
  useRedirect,
  useGetOne,
  useGetList,
  SimpleForm,
  DateInput,
  TextInput,
  required,
  NumberInput,
  SelectInput,
  ReferenceInput
} from 'react-admin';
import { Grid } from '@mui/material';
import moment from 'moment';
import 'moment/locale/fr';
import { RichTextInput } from 'ra-input-rich-text';
import { useLocation } from 'react-router-dom';

// --- Helper robuste pour extraire l'id du type (IRI / number / objet / hydra)
const getTypeId = (value) => {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = parseInt(value.split('/').pop(), 10);
    return Number.isFinite(n) ? n : undefined;
  }
  if (typeof value === 'object') {
    if (typeof value.id === 'number') return value.id;
    if (typeof value['@id'] === 'string') {
      const n = parseInt(value['@id'].split('/').pop(), 10);
      return Number.isFinite(n) ? n : undefined;
    }
  }
  return undefined;
};

const MaintenanceWorkCreate = () => {
  const redirect = useRedirect();

  // --- ID depuis le querystring (plus de window.location.hash)
  const location = useLocation();
  const id = React.useMemo(
      () => new URLSearchParams(location.search).get('id'),
      [location.search]
  );

  // --- Campagnes => choices (YYYY / YYYY)
  const { data: campaigns } = useGetList('campaigns', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  });

  const choices = React.useMemo(() => {
    if (!campaigns) return [];
    moment.locale('fr');
    return campaigns.map((campaign) => {
      const startDate = moment(campaign.startDate).format('YYYY');
      const endDate = moment(campaign.endDate).format('YYYY');
      return { id: campaign.id, name: `${startDate} / ${endDate}` };
    });
  }, [campaigns]);

  // --- Water body + déduction de isBasin depuis l'API
  const { data: waterBodyData } = useGetOne('water_bodies', { id });
  const isBasin = React.useMemo(() => {
    if (!waterBodyData) return false; // ou undefined si tu veux gérer un loader
    const typeId = getTypeId(waterBodyData.waterBodyType);
    return typeId === 2; // ⚠️ adapte si l'ID du type « Bassin » diffère
  }, [waterBodyData]);

  const onSuccess = () => {
    // Garde le param si ta liste l'utilise encore; sinon tu peux le supprimer.
    const basinParam = isBasin ? '&isBasin=true' : '';
    redirect({
      pathname: '/action_maintenance_works',
      search: `filter=${JSON.stringify({ waterBody: id })}${basinParam}`,
    });
  };

  let current = useListController();

  return (
      <Create mutationOptions={{ onSuccess }} mutationMode="pessimistic" sanitizeEmptyValues>
        <SimpleForm>
          <h1 className={"titleName"}>
            {current !== undefined ? current.defaultTitle : ''} - {waterBodyData !== undefined ? waterBodyData.name : ''}
          </h1>

          <Grid container>
            <Grid item xs={6}>
              <SelectInput source="campaign" choices={choices} label="Campagne" validate={[required()]} fullWidth />
            </Grid>
          </Grid>

          {isBasin && (
              <Grid container>
                <Grid item xs={6}>
                  <SelectInput
                      source="affiliation"
                      label="Affiliation de l'action"
                      choices={[
                        { id: 'Production estivale 1', name: 'Production estivale 1' },
                        { id: 'Production estivale 2', name: 'Production estivale 2' },
                        { id: 'Stockage hivernal 1', name: 'Stockage hivernal 1' },
                        { id: 'Stockage hivernal 2', name: 'Stockage hivernal 2' }
                      ]}
                      validate={[required()]}
                      fullWidth
                  />
                </Grid>
              </Grid>
          )}

          <Grid container>
            <Grid item xs={3}>
              <DateInput source="date" label="Date" validate={[required()]} fullWidth/>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={3}>
              <TextInput source="workDone" label="Nature de la charge" validate={[required()]} fullWidth/>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={3}>
              <NumberInput source="estimatedCost" label="Coût estimé (en € HT)" fullWidth/>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={6}>
              <TextInput source="note" label="Remarque(s)" multiline fullWidth />
            </Grid>
          </Grid>

          {waterBodyData !== undefined ? (
              <TextInput source="waterBody" className={"hidden"} defaultValue={waterBodyData.id} type={"hidden"} />
          ) : (
              ''
          )}
        </SimpleForm>
      </Create>
  );
};

export default MaintenanceWorkCreate;
